/**
 * Structure Dropdown Component
 * Dropdown for selecting document structure (Section, Subsection, etc.)
 */

import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { EditorView } from '@codemirror/view'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { insertStructure } from '@/utils/latex/insertionHelpers'

interface StructureDropdownProps {
  view: EditorView | null
  theme?: 'light' | 'dark'
}

type StructureType = 'normal' | 'section' | 'subsection' | 'subsubsection' | 'paragraph' | 'subparagraph'

const structureOptions: Array<{ value: StructureType; label: string; command: string }> = [
  { value: 'normal', label: 'Normal text', command: '' },
  { value: 'section', label: 'Section', command: '\\section{}' },
  { value: 'subsection', label: 'Subsection', command: '\\subsection{}' },
  { value: 'subsubsection', label: 'Subsubsection', command: '\\subsubsection{}' },
  { value: 'paragraph', label: 'Paragraph', command: '\\paragraph{}' },
  { value: 'subparagraph', label: 'Subparagraph', command: '\\subparagraph{}' },
]

const StructureDropdown: React.FC<StructureDropdownProps> = ({ view, theme = 'light' }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selected, setSelected] = useState<StructureType>('normal')
  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null)

  // Calculate dropdown position when opening
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left
      })
    } else {
      setDropdownPosition(null)
    }
  }, [isOpen])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      // Use a small delay to prevent immediate closing when opening
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside, true)
      }, 10)
      return () => {
        clearTimeout(timeoutId)
        document.removeEventListener('mousedown', handleClickOutside, true)
      }
    }
  }, [isOpen])

  const handleSelect = (value: StructureType) => {
    if (value === 'normal') {
      // For normal text, we could remove structure commands if present
      // For now, just close the dropdown
      setIsOpen(false)
      return
    }

    if (!view) return

    insertStructure(view, value)
    setSelected(value)
    setIsOpen(false)
    view.focus()
  }

  const selectedOption = structureOptions.find(opt => opt.value === selected)

  const buttonClass = clsx(
    'flex items-center gap-1 px-2 py-1.5 rounded transition-colors text-xs font-medium',
    theme === 'dark'
      ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
      : 'text-gray-800 hover:bg-gray-100 hover:text-gray-900',
    'disabled:opacity-50 disabled:cursor-not-allowed'
  )

  const dropdownClass = clsx(
    'absolute top-full left-0 mt-1 rounded-md shadow-xl z-[9999] min-w-[180px]',
    theme === 'dark'
      ? 'bg-gray-800 border border-gray-700'
      : 'bg-white border border-gray-200'
  )

  const optionClass = (value: StructureType) => clsx(
    'px-3 py-2 text-sm cursor-pointer transition-colors',
    theme === 'dark'
      ? value === selected
        ? 'bg-gray-700 text-white'
        : 'text-gray-100 hover:bg-gray-700'
      : value === selected
        ? 'bg-gray-100 text-gray-900'
        : 'text-gray-700 hover:bg-gray-50'
  )

  const dropdownContent = isOpen && dropdownPosition ? (
    <div 
      ref={dropdownRef}
      className={dropdownClass}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      style={{ 
        position: 'fixed',
        top: `${dropdownPosition.top}px`,
        left: `${dropdownPosition.left}px`,
        zIndex: 9999
      }}
    >
      {structureOptions.map((option) => (
        <div
          key={option.value}
          onClick={(e) => {
            e.stopPropagation()
            handleSelect(option.value)
          }}
          onMouseDown={(e) => e.stopPropagation()}
          className={optionClass(option.value)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              e.stopPropagation()
              handleSelect(option.value)
            }
          }}
        >
          {option.label}
        </div>
      ))}
    </div>
  ) : null

  return (
    <>
      <div className="relative flex-shrink-0">
        <button
          ref={buttonRef}
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setIsOpen(!isOpen)
          }}
          onMouseDown={(e) => e.stopPropagation()}
          disabled={!view}
          className={buttonClass}
          title="Document structure - Insert section, subsection, paragraph, etc."
          aria-label="Document structure"
        >
          <span>{selectedOption?.label || 'Normal text'}</span>
          <ChevronDownIcon className={clsx('w-3 h-3 transition-transform flex-shrink-0', isOpen && 'rotate-180')} />
        </button>
      </div>
      {dropdownContent && createPortal(dropdownContent, document.body)}
    </>
  )
}

export default StructureDropdown

